import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Sprout,
  ShieldCheck,
  GraduationCap,
  Microscope,
  HandshakeIcon,
  ExternalLink,
  ArrowRight,
  Mail,
  Building2,
  BookMarked,
  Users,
  Eye,
  Leaf,
} from 'lucide-react';

/**
 * /about — Public page (no auth gate). Designed for LinkedIn click-through
 * traffic and institutional credibility. Visual grammar matches AuthPage:
 * Cormorant Garamond italic headlines + Inter body, deep forest green
 * (#1F3A2E), warm cream background (#FAF6EE), sage-grey accents (#7A8B7A).
 */

const CREAM = '#FAF6EE';
const PAPER = '#FFFFFF';
const INK = '#1F3A2E';
const SAGE = '#2D6A4F';
const SAGE_MUTED = '#7A8B7A';
const CHARCOAL = '#2C2C2C';
const SOFT_GREEN = '#EAF2E5';
const FONT_HEADING = "'Cormorant Garamond', Georgia, serif";
const FONT_BODY = "'Inter', 'DM Sans', system-ui, sans-serif";

const METHODS = [
  {
    name: 'FAO-56 dual-Kc water balance',
    citation: 'Allen, Pereira, Raes & Smith (1998) FAO Irrigation & Drainage Paper 56',
    use: 'Reference ETo + crop water requirement (validated ±5–6 % vs lysimeter)',
  },
  {
    name: 'Single-sine degree-day phenology',
    citation: 'Baskerville & Emin (1969) Ecology 50(3): 514–517',
    use: 'GDD accumulation for crop and pest developmental stages',
  },
  {
    name: 'rrBLUP + GBLUP genomic selection',
    citation: 'Endelman (2011) Plant Genome 4(3): 250–255',
    use: 'GEBV prediction with 5-fold × 10-rep cross-validation',
  },
  {
    name: 'Bernardo (2014) rrBLUP + Fixed-QTL',
    citation: 'Bernardo (2014) Crop Science 54(1): 68–75',
    use: 'Genomic selection when major genes are known',
  },
  {
    name: 'Griffing\'s diallel methods I–IV',
    citation: 'Griffing (1956) Australian J. Biological Sciences 9: 463–493',
    use: 'GCA / SCA estimation for combining-ability studies',
  },
  {
    name: 'Generation mean analysis',
    citation: 'Mather & Jinks (1982); Hayman (1958)',
    use: '3-, 5-, and 6-parameter models with parsimony selection',
  },
  {
    name: 'Heterosis (MPH / BPH / SH)',
    citation: 'Fonseca & Patterson (1968); Meredith & Bridge (1972)',
    use: 'Cross-by-cross heterosis with significance testing',
  },
  {
    name: 'CACP cost-of-cultivation arithmetic',
    citation: 'Commission for Agricultural Costs & Prices, GoI',
    use: 'A2, A2+FL, C2 cost categories per crop per state',
  },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div
      style={{
        background: CREAM,
        minHeight: '100vh',
        fontFamily: FONT_BODY,
        color: CHARCOAL,
      }}
      data-testid="about-page"
    >
      {/* Minimal top bar (public — no SharedNav since user may not be logged in) */}
      <header
        className="flex items-center justify-between px-6 md:px-10 py-5"
        style={{ borderBottom: '1px solid rgba(31, 58, 46, 0.08)' }}
      >
        <Link to="/" className="flex items-center gap-2.5 select-none" data-testid="about-logo">
          <Leaf size={22} style={{ color: SAGE }} strokeWidth={2.2} />
          <span style={{ fontFamily: FONT_HEADING, fontSize: 22, color: INK, letterSpacing: '-0.01em' }}>
            BKAgriD
          </span>
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/predict"
            style={{ fontSize: 13, color: CHARCOAL, textDecoration: 'none', fontWeight: 500 }}
            data-testid="about-nav-predict"
          >
            Predict
          </Link>
          <Link
            to="/predict/validation"
            style={{ fontSize: 13, color: CHARCOAL, textDecoration: 'none', fontWeight: 500 }}
            data-testid="about-nav-validation"
          >
            Validation
          </Link>
          <button
            onClick={() => navigate('/login')}
            style={{
              fontSize: 13, fontWeight: 600, color: PAPER,
              background: SAGE, border: 'none',
              padding: '8px 16px', borderRadius: 6, cursor: 'pointer',
            }}
            data-testid="about-nav-signin"
          >
            Sign in
          </button>
        </div>
      </header>

      <main className="max-w-[920px] mx-auto px-6 md:px-10 py-16 md:py-24">

        {/* Hero / Founder section */}
        <section className="mb-20" data-testid="about-hero">
          <div
            style={{
              fontFamily: FONT_BODY, fontSize: 11, fontWeight: 600,
              color: SAGE_MUTED, letterSpacing: '0.18em',
              textTransform: 'uppercase', marginBottom: 22,
            }}
          >
            About BKAgriD
          </div>
          <h1
            style={{
              fontFamily: FONT_HEADING, fontStyle: 'italic',
              fontSize: 56, lineHeight: 1.06, color: INK,
              fontWeight: 500, letterSpacing: '-0.015em',
              marginBottom: 28, maxWidth: 720,
            }}
            data-testid="about-headline"
          >
            Built by a breeder, for breeders.
          </h1>
          <p
            style={{
              fontSize: 17, lineHeight: 1.7, color: CHARCOAL,
              fontWeight: 300, maxWidth: 680, marginBottom: 36,
            }}
          >
            BKAgriD is an open-science research platform created at <strong style={{ fontWeight: 600 }}>Agriculture University Jodhpur</strong>,
            in the arid heart of Rajasthan. It exists because the tools Indian plant breeders
            have been using did not. We chose peer-reviewed science over flashy AI, transparent
            methodology over black boxes, and an honest "we don't know" over a fabricated number.
          </p>

          {/* Founder card */}
          <div
            className="rounded-2xl p-7 md:p-8 flex flex-col md:flex-row gap-7 items-start"
            style={{ background: PAPER, border: '1px solid rgba(31, 58, 46, 0.08)' }}
            data-testid="about-founder"
          >
            {/* Initials avatar (placeholder for photo) */}
            <div
              className="shrink-0 flex items-center justify-center"
              style={{
                width: 96, height: 96, borderRadius: '50%',
                background: SOFT_GREEN, border: `1px solid ${SAGE}`,
              }}
            >
              <span style={{ fontFamily: FONT_HEADING, fontSize: 32, color: INK, fontWeight: 600 }}>
                PK
              </span>
            </div>
            <div className="flex-1">
              <h3
                style={{
                  fontFamily: FONT_HEADING, fontSize: 26, color: INK,
                  fontWeight: 500, marginBottom: 4, fontStyle: 'italic',
                }}
              >
                Dr. Praveen Kumar
              </h3>
              <p style={{ fontSize: 14, color: SAGE, fontWeight: 600, marginBottom: 10, letterSpacing: '0.01em' }}>
                Assistant Professor &middot; Genetics &amp; Plant Breeding
              </p>
              <p style={{ fontSize: 14, color: CHARCOAL, fontWeight: 500, marginBottom: 14 }}>
                Agriculture University Jodhpur (AU Jodhpur), Rajasthan
              </p>
              <p style={{ fontSize: 13.5, lineHeight: 1.65, color: SAGE_MUTED }}>
                Research focus: arid-zone crop breeding, biometrical genetics, and the
                methodology of plant-breeding tools. Working primarily on pearl millet,
                moth bean, cluster bean (guar), chickpea, sesame, and other Rajasthan-priority
                arid crops.
              </p>
            </div>
          </div>
        </section>

        {/* Mission / Origin Story */}
        <section className="mb-20" data-testid="about-mission">
          <SectionLabel>Mission</SectionLabel>
          <h2 style={headlineStyle()}>
            The tool was built because the tool didn&rsquo;t exist.
          </h2>
          <div className="space-y-6" style={{ fontSize: 15.5, lineHeight: 1.75, color: CHARCOAL, fontWeight: 300, maxWidth: 720 }}>
            <p>
              For an Indian plant breeder running a Diallel Method I, a Generation Mean
              Analysis, or a Genomic Selection trial, the standard workflow is: open R, find a
              decade-old package on CRAN, hope the dependencies still install, copy results into
              Excel, format the figure for a journal, and start over for the next analysis.
              The science is solid. The tool layer is broken.
            </p>
            <p>
              BKAgriD compresses that workflow into a single platform — Designs, Analysis,
              Molecular Genetics, PhenoScan, and now Predict — with publication-ready outputs
              that journals accept. Every formula traces back to its original-scientist
              citation. Every chart can be exported to DOCX, XLSX, or PDF without manual
              fiddling. And nothing in the stack uses statistics that wouldn&rsquo;t pass peer
              review.
            </p>
            <p>
              The Predict module continues this discipline. Indian agritech is full of
              dashboards quoting "AI-predicted yield" and "30-day price forecasts" with no error
              bands and no validation. We refuse to add to that pile. The trust infrastructure
              ships <em>before</em> the predictions.
            </p>
          </div>
        </section>

        {/* Methodological Lineage */}
        <section className="mb-20" data-testid="about-methods">
          <SectionLabel>Methodological lineage</SectionLabel>
          <h2 style={headlineStyle()}>
            Every formula, traceable to a paper.
          </h2>
          <p style={{ fontSize: 14, color: SAGE_MUTED, fontWeight: 300, maxWidth: 680, marginBottom: 28 }}>
            A non-exhaustive list. Every analytical method on BKAgriD is implemented from the
            original peer-reviewed source, with citations visible inside the platform.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {METHODS.map((m) => (
              <div
                key={m.name}
                className="rounded-xl p-5"
                style={{ background: PAPER, border: '1px solid rgba(31, 58, 46, 0.08)' }}
                data-testid={`method-${m.name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="flex items-start gap-2.5 mb-2">
                  <BookMarked size={14} style={{ color: SAGE, marginTop: 3, flexShrink: 0 }} />
                  <h4 style={{ fontSize: 14.5, color: INK, fontWeight: 600, lineHeight: 1.35 }}>
                    {m.name}
                  </h4>
                </div>
                <p style={{ fontSize: 12, color: SAGE_MUTED, fontStyle: 'italic', marginBottom: 6, marginLeft: 22 }}>
                  {m.citation}
                </p>
                <p style={{ fontSize: 12.5, color: CHARCOAL, lineHeight: 1.55, marginLeft: 22 }}>
                  {m.use}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Collaboration Network */}
        <section className="mb-20" data-testid="about-collaborators">
          <SectionLabel>Collaboration network</SectionLabel>
          <h2 style={headlineStyle()}>
            Open to ICAR, SAUs, and the breeding community.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <CollaboratorCard
              icon={GraduationCap}
              title="Agriculture University Jodhpur"
              role="Institutional home"
              body="Department of Genetics &amp; Plant Breeding. Where the platform was built and where its breeder-first design discipline comes from."
              testid="collab-au-jodhpur"
            />
            <CollaboratorCard
              icon={Microscope}
              title="ICAR-CAZRI Jodhpur"
              role="Validation pilot — pearl millet · moth bean · guar"
              body="Approached for validation pilot data on arid-zone crops. CAZRI scientists invited as co-authors on the methods paper."
              testid="collab-cazri"
            />
            <CollaboratorCard
              icon={Building2}
              title="Open invitation: ICAR institutes"
              role="National network"
              body="IIWBR (wheat), IIPR (pulses), IIMR (millets), DRR (rice), DRMR (mustard), NCIPM (pest forewarning) — open to validation collaborations."
              testid="collab-icar-open"
            />
            <CollaboratorCard
              icon={Users}
              title="Open invitation: State Agricultural Universities"
              role="Breeding network"
              body="Any SAU with cultivar trial data, breeding-program datasets, or methodology questions — collaborate with us. Co-authorship offered for validation contributions."
              testid="collab-sau-open"
            />
          </div>
        </section>

        {/* Validation Promise */}
        <section className="mb-20" data-testid="about-validation-promise">
          <div
            className="rounded-2xl p-8 md:p-10"
            style={{
              background: 'linear-gradient(135deg, #1F3A2E 0%, #2D6A4F 100%)',
              color: PAPER,
            }}
          >
            <div
              className="flex items-center gap-2 mb-4"
              style={{ color: '#A8E0BF', fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase' }}
            >
              <ShieldCheck size={13} />
              Public validation promise
            </div>
            <h2
              style={{
                fontFamily: FONT_HEADING, fontStyle: 'italic',
                fontSize: 36, fontWeight: 500, color: PAPER,
                lineHeight: 1.15, letterSpacing: '-0.012em', marginBottom: 18,
              }}
            >
              The dashboard exists before the predictions.
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.65, color: '#D8E5DC', fontWeight: 300, maxWidth: 640, marginBottom: 24 }}>
              Every Phase 1 module of the BKAgriD Predictive Analytics suite must pass an ICAR or
              SAU validation pilot before reaching production. The actual on-the-ground accuracy
              — RMSE, MAE, lead/lag — will be published per crop per state on a public dashboard.
              Empty today. Populated as pilots complete. We&rsquo;d rather publish a gap than
              market a number we cannot defend.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/predict/validation')}
                className="inline-flex items-center gap-2"
                style={{
                  fontSize: 13, fontWeight: 600, color: INK,
                  background: PAPER, border: 'none',
                  padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
                }}
                data-testid="about-cta-validation"
              >
                <Eye size={14} />
                See the Validation Dashboard
                <ArrowRight size={14} />
              </button>
              <button
                onClick={() => navigate('/predict')}
                className="inline-flex items-center gap-2"
                style={{
                  fontSize: 13, fontWeight: 600, color: PAPER,
                  background: 'transparent', border: '1px solid rgba(255,255,255,0.4)',
                  padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
                }}
                data-testid="about-cta-predict"
              >
                Read the Predict roadmap
                <ExternalLink size={13} />
              </button>
            </div>
          </div>
        </section>

        {/* Get Involved */}
        <section className="mb-12" data-testid="about-get-involved">
          <SectionLabel>Get involved</SectionLabel>
          <h2 style={headlineStyle()}>
            How to work with us.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <InvolvedCard
              icon={Sprout}
              title="Researchers &amp; breeders"
              body="Sign up for free and run your own designs, biometrical analyses, molecular pipelines, and PhenoScan workflows. No watermarks, no paywalls on core analyses."
              cta="Try BKAgriD"
              onClick={() => navigate('/login')}
              testid="involved-researcher"
            />
            <InvolvedCard
              icon={Building2}
              title="Institutions"
              body="ICAR institutes, SAUs, breeding programs — pilot the platform with your team. Custom onboarding, methodology review sessions, dataset workflows."
              cta="Contact for pilot"
              onClick={() => navigate('/predict')}
              testid="involved-institution"
            />
            <InvolvedCard
              icon={HandshakeIcon}
              title="Validation collaborators"
              body="Have field-trial data we could validate the Predict modules against? Get co-authorship on the methods paper documenting BKAgriD&rsquo;s validation pilots."
              cta="Co-author with us"
              onClick={() => navigate('/predict/validation')}
              testid="involved-collaborator"
            />
          </div>
        </section>

        {/* Footer signature */}
        <div
          className="text-center pt-12 mt-12"
          style={{ borderTop: '1px solid rgba(31, 58, 46, 0.08)' }}
          data-testid="about-footer-signature"
        >
          <p
            style={{
              fontFamily: FONT_HEADING, fontStyle: 'italic',
              fontSize: 17, color: SAGE_MUTED, fontWeight: 400, marginBottom: 6,
            }}
          >
            Built for researchers, breeders, and scholars who value rigor.
          </p>
          <p style={{ fontSize: 12, color: SAGE_MUTED, fontWeight: 400, letterSpacing: '0.05em' }}>
            BKAgriD &middot; Agriculture University Jodhpur &middot; Rajasthan, India
          </p>
        </div>
      </main>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontFamily: FONT_BODY, fontSize: 11, fontWeight: 600,
        color: SAGE, letterSpacing: '0.18em',
        textTransform: 'uppercase', marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function headlineStyle() {
  return {
    fontFamily: FONT_HEADING, fontStyle: 'italic',
    fontSize: 38, lineHeight: 1.12, color: INK,
    fontWeight: 500, letterSpacing: '-0.012em',
    marginBottom: 18, maxWidth: 720,
  };
}

function CollaboratorCard({ icon: Icon, title, role, body, testid }) {
  return (
    <div
      className="rounded-xl p-6"
      style={{ background: PAPER, border: '1px solid rgba(31, 58, 46, 0.08)' }}
      data-testid={testid}
    >
      <div
        className="inline-flex items-center justify-center mb-4"
        style={{ width: 38, height: 38, borderRadius: 10, background: SOFT_GREEN, color: SAGE }}
      >
        <Icon size={18} strokeWidth={2} />
      </div>
      <h3 style={{ fontFamily: FONT_HEADING, fontSize: 19, color: INK, fontWeight: 500, marginBottom: 3 }}>
        {title}
      </h3>
      <p style={{ fontSize: 12, color: SAGE, fontWeight: 600, marginBottom: 10, letterSpacing: '0.01em' }}>
        {role}
      </p>
      <p style={{ fontSize: 13, color: CHARCOAL, lineHeight: 1.6, fontWeight: 300 }}>
        {body}
      </p>
    </div>
  );
}

function InvolvedCard({ icon: Icon, title, body, cta, onClick, testid }) {
  return (
    <div
      className="rounded-xl p-6 flex flex-col h-full"
      style={{ background: PAPER, border: '1px solid rgba(31, 58, 46, 0.08)' }}
      data-testid={testid}
    >
      <div
        className="inline-flex items-center justify-center mb-4"
        style={{ width: 38, height: 38, borderRadius: 10, background: SOFT_GREEN, color: SAGE }}
      >
        <Icon size={18} strokeWidth={2} />
      </div>
      <h3 style={{ fontFamily: FONT_HEADING, fontSize: 19, color: INK, fontWeight: 500, marginBottom: 8 }}>
        {title}
      </h3>
      <p style={{ fontSize: 13, color: CHARCOAL, lineHeight: 1.6, fontWeight: 300, marginBottom: 16, flexGrow: 1 }}>
        {body}
      </p>
      <button
        onClick={onClick}
        className="inline-flex items-center gap-1.5 self-start"
        style={{
          fontSize: 12.5, fontWeight: 600, color: SAGE,
          background: 'transparent', border: 'none',
          padding: 0, cursor: 'pointer',
        }}
      >
        {cta}
        <ArrowRight size={13} />
      </button>
    </div>
  );
}
